import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

/**
 * Handles the magic link callback: /auth/magic?token=...
 * Verifies the token with the API and logs the member in.
 */
export default function MagicLinkVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  const [status, setStatus] = useState('verifying'); // 'verifying' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setErrorMsg('No sign-in token found in this link. Please request a new one.');
      return;
    }

    api.post('/auth/magic-link/verify', { token })
      .then(async (res) => {
        const { accessToken, member } = res.data;
        await loginWithToken(accessToken, member);
        // First-ever sign-in: no password set yet → send to password setup
        if (!member.has_password) {
          navigate('/auth/set-password', { replace: true });
        } else {
          let next = '/dashboard';
          try {
            const saved = JSON.parse(localStorage.getItem('after_sign_in') || 'null');
            localStorage.removeItem('after_sign_in');
            if (saved?.path?.startsWith('/') && Date.now() - saved.at < 60 * 60 * 1000) next = saved.path;
          } catch { /* fine without it */ }
          navigate(next, { replace: true });
        }
      })
      .catch(err => {
        setStatus('error');
        setErrorMsg(
          err.response?.data?.error
          || 'This sign-in link has expired or already been used.'
        );
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'verifying') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-reset-cream">
        <div className="text-center space-y-3">
          <div className="text-4xl animate-pulse">🔑</div>
          <p className="text-reset-brown font-medium">Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-reset-cream px-4">
      <div className="w-full max-w-md text-center space-y-4">
        <div className="text-4xl">⚠️</div>
        <h2 className="text-xl font-bold text-reset-brown">Link expired</h2>
        <p className="text-sm text-gray-600">{errorMsg}</p>
        <a
          href="/auth/login"
          className="inline-block bg-reset-terracotta text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:opacity-90 transition"
        >
          Request a new sign-in link
        </a>
      </div>
    </div>
  );
}
